import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTool } from "../hooks/useTools";
import ToolForm from "../components/ToolForm";
import QRGeneratorTool from "../tools/QRGeneratorTool";
import PDFMergerTool from "../tools/PDFMergerTool";
import PDFSplitterTool from "../tools/PDFSplitterTool";
import PasswordGeneratorTool from "../tools/PasswordGeneratorTool";
import * as api from "../services/api";
import { downloadCsv, downloadJson, openPrintableResult } from "../services/export";
import { trackAuthed } from "../services/events";

export default function ToolDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { tool, loading: toolLoading, error: toolError } = useTool(slug || "");
  const resultsRef = React.useRef<HTMLDivElement | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Detect browser-only tools even if DB schema flag is missing.
  let parsedSchema: Record<string, unknown> = {};
  try { parsedSchema = JSON.parse(tool?.config_schema || "{}"); } catch {}
  const isBrowserOnly =
    parsedSchema._browserOnly === true ||
    tool?.slug === "qr-generator" ||
    tool?.slug === "pdf-merger";
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");

  const loadRuns = async () => {
    if (!slug) return;
    if (isBrowserOnly || !hasToken) {
      setRuns([]);
      return;
    }
    setRunsLoading(true);
    try {
      const data = await api.getToolRuns(slug);
      setRuns(data.runs || []);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  };

  React.useEffect(() => {
    loadRuns().catch(() => {});
  }, [slug, isBrowserOnly, hasToken]);

  React.useEffect(() => {
    if (!slug || !tool?.name) return;
    trackAuthed("tool_opened", { slug, toolName: tool.name });
  }, [slug, tool?.name]);

  const handleSubmit = async (data: Record<string, any>) => {
    if (!slug) return;
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      trackAuthed("tool_run_started", { slug });
      const res = await api.runTool(slug, data);
      setResult(res);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      trackAuthed("tool_run_completed", {
        slug,
        status: res.status,
        duration_ms: res.duration_ms,
      });
      await loadRuns();
    } catch (err: any) {
      setError(err.message || "Tool execution failed");
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      trackAuthed("tool_run_failed", {
        slug,
        message: err?.message || "Tool execution failed",
      });
    } finally {
      setRunning(false);
    }
  };

  const filteredRuns = runs.filter((run) => {
    if (statusFilter !== "all" && run.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(run.id || "").toLowerCase().includes(q) ||
      String(run.status || "").toLowerCase().includes(q)
    );
  });

  const exportResult = (kind: "json" | "csv" | "pdf") => {
    if (!result) return;
    const safeSlug = slug || "tool";

    if (kind === "json") {
      downloadJson(safeSlug + "-result.json", result);
    }

    if (kind === "csv") {
      const rows: Array<Record<string, unknown>> = [];
      rows.push({
        run_id: result.run_id || "",
        status: result.status || "",
        duration_ms: result.duration_ms || 0,
        generated_at: new Date().toISOString(),
      });
      rows.push({
        result: result.result || result,
      });
      downloadCsv(safeSlug + "-result.csv", rows);
    }

    if (kind === "pdf") {
      openPrintableResult((tool?.name || safeSlug) + " Result", result);
    }

    trackAuthed("tool_result_exported", { slug: safeSlug, kind });
  };

  const exportRunsCsv = () => {
    if (!filteredRuns.length || !slug) return;
    const rows = filteredRuns.map((run) => ({
      id: run.id,
      status: run.status,
      duration_ms: run.duration_ms,
      created_at: run.created_at,
      error: run.error || "",
      input: run.input || "",
      output: run.output || "",
    }));
    downloadCsv(slug + "-runs.csv", rows);
    trackAuthed("tool_runs_exported", { slug, count: rows.length });
  };

  if (toolLoading) return <div className="max-w-3xl mx-auto px-4 py-12"><p>Loading...</p></div>;
  if (toolError) return <div className="max-w-3xl mx-auto px-4 py-12"><p className="text-red-600">{toolError}</p></div>;
  if (!tool) return null;

  const categoryColors: Record<string, string> = {
    finance: "bg-green-100 text-green-800",
    analytics: "bg-blue-100 text-blue-800",
    marketing: "bg-orange-100 text-orange-800",
    design: "bg-purple-100 text-purple-800",
    developer: "bg-gray-100 text-gray-800",
    productivity: "bg-teal-100 text-teal-800",
  };

  const handleBrowserRunRecorded = async (_input: Record<string, unknown>, _output: unknown) => {
    // Browser-only tools should stay fully client-side to avoid auth/rate-limit noise in console.
    trackAuthed("tool_run_completed", { slug: slug || "", browserOnly: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Page header bar ── */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={"text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide " + (categoryColors[tool.category] || "bg-gray-100 text-gray-800")}>
              {tool.category}
            </span>
            {tool.is_premium === 1 && (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white">⭐ PRO</span>
            )}
            {isBrowserOnly && (
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-sky-100 text-sky-800">🌐 Browser Tool</span>
            )}
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">{tool.name}</h1>
          <p className="text-gray-500 text-lg max-w-3xl">{tool.description}</p>
        </div>
      </div>

      {/* ── Main two-column area ── */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex flex-col xl:flex-row gap-8 items-start">

          {/* ── Left: Form ── */}
          <div className="w-full xl:w-[36%] shrink-0">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <h2 className="font-semibold text-gray-900 text-lg">Configure & Run</h2>
              </div>
              <div className="p-6 lg:p-8">
                {isBrowserOnly ? (
                  <>
                    {tool.slug === "qr-generator" && <QRGeneratorTool onRunRecorded={handleBrowserRunRecorded} />}
                    {tool.slug === "pdf-merger" && <PDFMergerTool onRunRecorded={handleBrowserRunRecorded} />}
                    {tool.slug === "pdf-splitter" && <PDFSplitterTool onRunRecorded={handleBrowserRunRecorded} />}
                    {tool.slug === "password-generator" && <PasswordGeneratorTool onRunRecorded={handleBrowserRunRecorded} />}
                  </>
                ) : (
                  <ToolForm configSchema={tool.config_schema} onSubmit={handleSubmit} loading={running} />
                )}
              </div>
            </div>
          </div>

          {/* ── Right: Results ── */}
          <div ref={resultsRef} className="w-full xl:flex-1 min-w-0">
            {!isBrowserOnly && error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-5 mb-6 flex gap-3">
                <span className="text-xl">⚠️</span>
                <div>
                  <p className="font-semibold">Error</p>
                  <p className="text-sm mt-0.5">{error}</p>
                </div>
              </div>
            )}

            {!result && !error && !running && !isBrowserOnly && (
              <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-12 text-center text-gray-400">
                <p className="text-5xl mb-3">⚡</p>
                <p className="font-semibold text-gray-500 text-lg">Results will appear here</p>
                <p className="text-sm mt-1">Fill in the form and click Run to see your results.</p>
              </div>
            )}

            {running && (
              <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
                <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-500 font-medium">Running…</p>
              </div>
            )}

            {result && !running && (
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h2 className="font-semibold text-gray-900 text-lg">Results</h2>
                    {result.duration_ms && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{result.duration_ms}ms</span>
                    )}
                    {result.status && (
                      <span className={"text-xs font-bold px-2.5 py-1 rounded-full " + (result.status === "completed" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700")}>
                        {result.status}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => exportResult("json")} className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition">⬇ JSON</button>
                    <button onClick={() => exportResult("csv")} className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition">⬇ CSV</button>
                    <button onClick={() => exportResult("pdf")} className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition">🖨 Print</button>
                  </div>
                </div>
                <div className="p-6 lg:p-8">
                  <ResultViewer data={result} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Full-width: Run History ── */}
        {!isBrowserOnly && (
          <div className="mt-10 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-semibold text-gray-900 text-lg">Run History</h2>
              <div className="flex gap-2">
                <button onClick={() => loadRuns()} className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition">↻ Refresh</button>
                <button onClick={exportRunsCsv} disabled={!filteredRuns.length} className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition">⬇ Export CSV</button>
              </div>
            </div>
            <div className="px-6 pt-4 pb-2 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-72 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="Search by run id or status…"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-44 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="all">All statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="px-6 pb-6">
              {runsLoading ? (
                <p className="text-sm text-gray-400 py-4">Loading run history…</p>
              ) : filteredRuns.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No runs found. Run the tool to see history here.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredRuns.slice(0, 20).map((run) => (
                    <div key={run.id} className="py-4">
                      <div className="flex flex-wrap gap-3 text-sm items-center mb-1">
                        <code className="text-xs font-mono text-gray-400">{run.id}</code>
                        <span className={"px-2.5 py-0.5 rounded-full text-xs font-semibold " + (run.status === "completed" ? "bg-green-100 text-green-800" : run.status === "failed" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700")}>{run.status}</span>
                        <span className="text-gray-400 text-xs">{run.duration_ms || 0}ms</span>
                        <span className="text-gray-400 text-xs">{new Date(run.created_at).toLocaleString()}</span>
                      </div>
                      {run.error && <p className="text-xs text-red-600 mb-2">{run.error}</p>}
                      <details>
                        <summary className="cursor-pointer text-xs text-indigo-500 hover:text-indigo-700">View payload</summary>
                        <pre className="bg-gray-50 rounded-xl p-4 mt-2 overflow-auto text-xs leading-relaxed">
                          {JSON.stringify({ input: run.input, output: run.output }, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Result viewer ─────────────────────────────────────────────────────────────

const SKIP_KEYS = new Set(["run_id", "status", "duration_ms", "success", "message"]);

const KEY_ALIASES: Record<string, string> = {
  roi: "ROI", emi: "EMI", var: "VaR", id: "ID", ids: "IDs",
  api: "API", url: "URL", json: "JSON", csv: "CSV", pdf: "PDF",
  apr: "APR", irr: "IRR", std: "Std", ltv: "LTV", pct: "%", dev: "Dev",
};

// Multi-word phrase aliases applied before splitting
const PHRASE_ALIASES: Array<[RegExp, string]> = [
  [/\bstd[_ ]dev\b/gi, "Std Dev"],
];

function humanizeKey(raw: string): string {
  let key = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  for (const [pattern, replacement] of PHRASE_ALIASES) {
    key = key.replace(pattern, replacement);
  }
  return key
    .split(" ")
    .map((w) => {
      const lower = w.toLowerCase();
      return KEY_ALIASES[lower] ?? (w ? w[0].toUpperCase() + w.slice(1) : "");
    })
    .join(" ");
}

function isScalarVal(v: unknown): v is string | number | boolean | null | undefined {
  return v === null || v === undefined || typeof v !== "object";
}

function fmtValue(val: unknown, key = ""): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") {
    const k = key.toLowerCase();
    const isPct =
      k.includes("percent") || k.includes("rate") || k.includes("allocation") ||
      k.includes("probability") || k.endsWith("_pct") || k.endsWith("_ratio") ||
      k === "sharpe_ratio" || k.includes("contribution");
    const isMoney =
      k.includes("value") || k.includes("price") || k.includes("cost") ||
      k.includes("amount") || k.includes("investment") || k.includes("var_") ||
      k.startsWith("p5") || k.startsWith("p1") || k.startsWith("p2") ||
      k.startsWith("p7") || k.startsWith("p9") || k.includes("expected") ||
      k.includes("median") || k.includes("worst") || k.includes("best") ||
      k.includes("emi") || k.includes("principal") || k.includes("interest");
    if (isPct) return val.toFixed(2) + "%";
    if (isMoney) {
      const abs = Math.abs(val);
      return "$" + val.toLocaleString(undefined, { maximumFractionDigits: abs >= 1000 ? 0 : 2 });
    }
    const abs = Math.abs(val);
    const dec = abs >= 1000 ? 0 : abs >= 10 ? 1 : 2;
    return val.toLocaleString(undefined, { maximumFractionDigits: dec });
  }
  if (typeof val === "string") {
    if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
      const d = new Date(val);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
    return val;
  }
  return String(val);
}

function numSentiment(key: string, value: unknown): "pos" | "neg" | "neutral" {
  if (typeof value !== "number") return "neutral";
  const k = key.toLowerCase();
  if (k.includes("loss") || k.includes("var_") || k.includes("worst")) return value < 0 ? "neg" : "pos";
  if (
    k.includes("return") || k.includes("value") || k.includes("sharpe") ||
    k.includes("profit") || k.includes("best") || k.includes("expected")
  ) return value >= 0 ? "pos" : "neg";
  return "neutral";
}

function MetricCard({ label, rawKey, value }: { label: string; rawKey: string; value: unknown }) {
  const s = numSentiment(rawKey, value);
  const numClass = s === "pos" ? "text-emerald-600" : s === "neg" ? "text-red-500" : "text-gray-900";
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1 min-w-0">
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide leading-none truncate">{label}</span>
      <span className={"text-xl font-bold leading-tight break-words " + numClass}>{fmtValue(value, rawKey)}</span>
    </div>
  );
}

function MetricGrid({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj).filter(([, v]) => isScalarVal(v));
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {entries.map(([k, v]) => (
        <MetricCard key={k} label={humanizeKey(k)} rawKey={k} value={v} />
      ))}
    </div>
  );
}

function ObjectTable({ rows }: { rows: Array<Record<string, unknown>> }) {
  if (!rows.length) return <p className="text-sm text-gray-400 p-4">No items.</p>;
  const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r || {}))));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {keys.map((k) => (
              <th key={k} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                {humanizeKey(k)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/60"}>
              {keys.map((k) => {
                const v = row[k];
                const s = numSentiment(k, v);
                return (
                  <td key={k} className={
                    "px-4 py-3 font-medium whitespace-nowrap " +
                    (s === "pos" ? "text-emerald-700" : s === "neg" ? "text-red-600" : "text-gray-800")
                  }>
                    {isScalarVal(v) ? fmtValue(v, k) : <span className="text-gray-400 italic text-xs">{JSON.stringify(v)}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function renderValue(key: string, val: unknown): React.ReactNode {
  if (Array.isArray(val)) {
    if (val.length === 0) return <p className="text-sm text-gray-400">—</p>;
    if (typeof val[0] === "object" && val[0] !== null) {
      return <ObjectTable rows={val as Array<Record<string, unknown>>} />;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {val.map((item, i) => (
          <span key={i} className="bg-indigo-50 text-indigo-700 text-sm font-medium px-3 py-1 rounded-full border border-indigo-100">
            {fmtValue(item, key)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof val === "object" && val !== null) {
    const obj = val as Record<string, unknown>;
    const scalars: Record<string, unknown> = {};
    const nested: Array<[string, unknown]> = [];
    for (const [k, v] of Object.entries(obj)) {
      if (isScalarVal(v)) scalars[k] = v;
      else nested.push([k, v]);
    }
    return (
      <div className="space-y-4">
        {Object.keys(scalars).length > 0 && <MetricGrid obj={scalars} />}
        {nested.map(([k, v]) => (
          <div key={k}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{humanizeKey(k)}</p>
            {renderValue(k, v)}
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-base font-semibold text-gray-900">{fmtValue(val, key)}</span>;
}

function ResultViewer({ data }: { data: any }) {
  if (!data) return null;

  // Strip the outer run-wrapper
  const wrap: Record<string, unknown> =
    typeof data === "object" && data !== null ? (data.result ?? data) : {};

  if (typeof wrap !== "object" || wrap === null) {
    return <pre className="bg-gray-50 rounded-xl p-5 overflow-auto text-sm font-mono leading-relaxed">{String(wrap)}</pre>;
  }

  // Collect the meaningful payload (skip run metadata keys; unwrap single 'data' envelope)
  const entries = Object.entries(wrap).filter(([k]) => !SKIP_KEYS.has(k));
  const payload: Array<[string, unknown]> =
    entries.length === 1 && entries[0][0] === "data" && typeof entries[0][1] === "object" && entries[0][1] !== null
      ? Object.entries(entries[0][1] as Record<string, unknown>).filter(([k]) => !SKIP_KEYS.has(k))
      : entries;

  if (payload.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        This tool completed but returned no displayable output.
      </div>
    );
  }

  // Separate top-level scalars (go in a big headline grid) from sections
  const topScalars: Array<[string, unknown]> = payload.filter(([, v]) => isScalarVal(v));
  const sections: Array<[string, unknown]> = payload.filter(([, v]) => !isScalarVal(v));

  return (
    <div className="space-y-5">
      {topScalars.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {topScalars.map(([k, v]) => (
            <MetricCard key={k} label={humanizeKey(k)} rawKey={k} value={v} />
          ))}
        </div>
      )}

      {sections.map(([k, v]) => (
        <SectionCard key={k} title={humanizeKey(k)}>
          {Array.isArray(v) ? (
            typeof (v as unknown[])[0] === "object"
              ? <ObjectTable rows={v as Array<Record<string, unknown>>} />
              : renderValue(k, v)
          ) : (
            renderValue(k, v)
          )}
        </SectionCard>
      ))}
    </div>
  );
}